import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Trash2, Loader2, Info, Clock } from "lucide-react";
import { toast } from "sonner";
import SettingsLayout from "./SettingsLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { CURRENCIES } from "@/lib/format";
import api, { apiError } from "@/lib/api";

export default function AccountSettings() {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || "");
  const [currency, setCurrency] = useState(user?.default_currency || "NGN");
  const [saving, setSaving] = useState(false);

  const [delOpen, setDelOpen] = useState(false);
  const [delPassword, setDelPassword] = useState("");
  const [deleting, setDeleting] = useState(false);

  const [emailOpen, setEmailOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [emailPassword, setEmailPassword] = useState("");
  const [changingEmail, setChangingEmail] = useState(false);

  const dirty = name.trim() !== (user?.name || "") || currency !== (user?.default_currency || "NGN");

  const save = async () => {
    if (!name.trim()) {
      toast.error("Please enter your name.");
      return;
    }
    setSaving(true);
    try {
      await api.patch("/auth/profile", { name: name.trim(), default_currency: currency });
      await refreshUser();
      toast.success("Account updated");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const changeEmail = async () => {
    if (!newEmail.trim() || !emailPassword) {
      toast.error("Enter your new email and password.");
      return;
    }
    setChangingEmail(true);
    try {
      await api.post("/auth/change-email", { new_email: newEmail.trim(), password: emailPassword });
      await refreshUser();
      toast.success("Verification email sent to your new address.");
      setEmailOpen(false);
      setNewEmail("");
      setEmailPassword("");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setChangingEmail(false);
    }
  };

  const deleteAccount = async () => {
    if (!delPassword) {
      toast.error("Enter your password to confirm.");
      return;
    }
    setDeleting(true);
    try {
      await api.delete("/auth/account", { data: { password: delPassword } });
      toast.success("Your account has been deleted.");
      await logout();
      navigate("/welcome", { replace: true });
    } catch (e) {
      toast.error(apiError(e));
      setDeleting(false);
    }
  };

  return (
    <SettingsLayout title="Account" subtitle="Your name, email and currency" testId="account-settings-page">
      <div className="space-y-5">
        <div>
          <Label htmlFor="acc-name" className="text-sm font-medium text-foreground">Full name</Label>
          <Input
            id="acc-name"
            data-testid="account-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            className="mt-1.5 h-12 rounded-xl"
          />
        </div>

        <div>
          <Label className="text-sm font-medium text-foreground">Email address</Label>
          <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-border bg-secondary/60 px-4 h-12">
            <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
            <span data-testid="account-email" className="text-sm text-foreground truncate flex-1">{user?.email}</span>
            <button
              data-testid="account-change-email-button"
              onClick={() => setEmailOpen(true)}
              className="text-xs font-bold text-accent shrink-0"
            >
              Change
            </button>
          </div>
          {user?.pending_email ? (
            <p className="mt-1.5 text-xs text-amber-700 flex items-start gap-1" data-testid="account-pending-email">
              <Clock className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              Pending verification: {user.pending_email}. Your current email stays active until the new one is verified.
            </p>
          ) : (
            <p className="mt-1.5 text-xs text-muted-foreground flex items-start gap-1">
              <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              We'll send a verification link to your new address before it takes effect.
            </p>
          )}
        </div>

        <div>
          <Label className="text-sm font-medium text-foreground">Default currency</Label>
          <Select value={currency} onValueChange={setCurrency}>
            <SelectTrigger data-testid="account-currency-select" className="mt-1.5 h-12 rounded-xl">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CURRENCIES.map((c) => (
                <SelectItem key={c.code} value={c.code} data-testid={`currency-option-${c.code}`}>
                  {c.symbol} {c.label} ({c.code})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="mt-1.5 text-xs text-muted-foreground">Used to display amounts and for new records.</p>
        </div>

        <Button
          data-testid="account-save-button"
          onClick={save}
          disabled={saving || !dirty}
          className="w-full h-12 rounded-xl text-base"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {saving ? "Saving…" : "Save changes"}
        </Button>

        <div className="pt-4 border-t border-border">
          <p className="text-sm font-semibold text-foreground">Delete account</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Permanently removes your account and all your records. This cannot be undone.
          </p>
          <Button
            data-testid="account-delete-button"
            variant="outline"
            onClick={() => setDelOpen(true)}
            className="mt-3 w-full h-11 rounded-xl border-red-200 bg-red-50 text-red-600 hover:bg-red-100"
          >
            <Trash2 className="h-4 w-4" /> Delete my account
          </Button>
        </div>
      </div>

      <Dialog open={emailOpen} onOpenChange={(o) => { setEmailOpen(o); if (!o) { setNewEmail(""); setEmailPassword(""); } }}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">Change email address</DialogTitle>
            <DialogDescription>
              We'll send a verification link to the new address. Your current email stays active until you verify the new one.
            </DialogDescription>
          </DialogHeader>
          <Input
            data-testid="change-email-input"
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            onFocus={(e) => setTimeout(() => e.target.scrollIntoView({ block: "center", behavior: "smooth" }), 300)}
            placeholder="New email address"
            className="h-12 rounded-xl"
          />
          <Input
            data-testid="change-email-password"
            type="password"
            value={emailPassword}
            onChange={(e) => setEmailPassword(e.target.value)}
            onFocus={(e) => setTimeout(() => e.target.scrollIntoView({ block: "center", behavior: "smooth" }), 300)}
            placeholder="Your current password"
            className="h-12 rounded-xl"
          />
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setEmailOpen(false)} className="h-11 rounded-xl">Cancel</Button>
            <Button data-testid="change-email-confirm" onClick={changeEmail} disabled={changingEmail} className="h-11 rounded-xl">
              {changingEmail ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {changingEmail ? "Sending…" : "Send verification"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={delOpen} onOpenChange={(o) => { setDelOpen(o); if (!o) setDelPassword(""); }}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">Delete your account?</DialogTitle>
            <DialogDescription>
              This permanently deletes your account and every record you've saved. Enter your password to confirm.
            </DialogDescription>
          </DialogHeader>
          <Input
            data-testid="account-delete-password"
            type="password"
            value={delPassword}
            onChange={(e) => setDelPassword(e.target.value)}
            placeholder="Your password"
            className="h-12 rounded-xl"
          />
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setDelOpen(false)} className="h-11 rounded-xl">Cancel</Button>
            <Button
              data-testid="account-delete-confirm"
              onClick={deleteAccount}
              disabled={deleting}
              className="h-11 rounded-xl bg-red-600 hover:bg-red-700 text-white"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              {deleting ? "Deleting…" : "Delete account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SettingsLayout>
  );
}
